import React, {FunctionComponent, useState, useEffect} from "react"
import {HistoryDto, StartedEventFlowHistoryDto} from "@murphy-frontend/common/interfaces/IDroneEventsApi"
import {ColumnHeaderCell, ColumnRowCell} from "@murphy-frontend/web-core/components/Table/Models"
import Table from "@murphy-frontend/web-core/components/Table"
import dayjs from "dayjs"
import {TimeZoneType, getConvertedDateDayjs} from "@murphy-frontend/common/services/TimeService"
import {BrowserRouter} from "react-router-dom"

interface HistoryChange extends HistoryDto {
	change: JSX.Element
}
interface EventHistoryTableProps {
	history?: HistoryDto[]
	timeZone: TimeZoneType
	eventFlowHistory: StartedEventFlowHistoryDto[]
	translations: Record<any, any>
}

const EventHistoryTable: FunctionComponent<EventHistoryTableProps> = ({
	history,
	timeZone,
	eventFlowHistory,
	translations
}) => {
	const [tableData, setTableData] = useState<ColumnRowCell[][]>([])

	const HistoryColumns: ColumnHeaderCell[] = [
		{
			columnKey: "timestamp",
			displayName: translations.timestamp,
			isSortable: true,
			size: "column-large"
		},
		{
			columnKey: "editor",
			displayName: translations.editor,
			isSortable: true,
			size: "column-large"
		},
		{
			columnKey: "change",
			displayName: translations.whatChanged,
			isSortable: false, // update type for sorting
			size: "column-medium"
		}
	]

	const factorChanges = (his: HistoryDto[], eh: StartedEventFlowHistoryDto[]) => {
		// sort table and render changes to column
		let obj: {[key: number]: HistoryDto[]} = {}

		his.map(a => {
			if (!obj[`${a.BlockId}`]) {
				obj = {
					...obj,
					[a.BlockId]: [{...a}]
				}
			} else {
				obj[`${a.BlockId}`].push(a)
			}
		})

		let temp: HistoryChange[] = []
		Object.keys(obj).map((key: string) => {
			const ip = obj[parseInt(key)]
			let tempo = ip.map((c, idx) => {
				if (c.BlockType === 1) {
					if (idx !== ip.length - 1) {
						return {
							...c,
							change: (
								<span className='event-history-change'>
									{ip[idx + 1].InputText ? (
										<>
											<em>Changed &nbsp;</em>
											<span>{ip[idx + 1].InputText}</span>
											<br />
											<span>{`to '${c.InputText}'`}</span>
											<br />
											<span>{`in ${c.BlockTitle}`}</span>
										</>
									) : (
										<>
											<em>Added &nbsp;</em>
											<span>{c.InputText}</span>
											<br />
											<span>{`in ${c.BlockTitle}`}</span>
										</>
									)}
								</span>
							)
						}
					} else {
						return {
							...c,
							change: (
								<span className='event-history-change'>
									<em>Added &nbsp;</em>
									<span>{c.Title}</span>
									<br />
									<span>{`in ${c.BlockTitle}`}</span>
								</span>
							)
						}
					}
				} else {
					if (idx === ip.length - 1) {
						return {
							...c,
							change: (
								<span className='event-history-change'>
									<em>Added &nbsp;</em>
									<span>{c.Text}</span>
									<br />
									<span>{`in ${c.BlockTitle}`}</span>
								</span>
							)
						}
					}
					return {
						...c,
						change: (
							<span className='event-history-change'>
								<em>{c.Checked ? "Checked" : "Unchecked"}&nbsp;</em>
								<span>{c.Text}</span>
								<br />
								<span>{`in ${c.BlockTitle}`}</span>
							</span>
						)
					}
				}
			})
			temp.push(...tempo)
		})

		const ehRows = eh.map((e, idx) => {
			if (idx === eh.length - 1) {
				return {
					...e,
					change: (
						<span className='event-history-change'>
							<em>{"Started"}&nbsp;</em>
							<span>the incident</span>
						</span>
					)
				}
			} else if (e.Completed) {
				return {
					...e,
					change: (
						<span className='event-history-change'>
							<em>{"Closed"}&nbsp;</em>
							<span>the incident</span>
						</span>
					)
				}
			} else {
				return {
					...e,
					change: (
						<span className='event-history-change'>
							<em>{"Re-opened"}&nbsp;</em>
							<span>the incident</span>
						</span>
					)
				}
			}
		})

		const rows = [...temp, ...ehRows].sort((a, b) => dayjs(b.Time).diff(dayjs(a.Time)))
		const data = rows.map(his => {
			return [
				{
					id: his.Key,
					rowId: his.Key,
					displayValue: getConvertedDateDayjs(his.Time, timeZone).format("DD MMM YYYY, HH:mm"),
					value: his.Time,
					columnKey: "timestamp"
				},
				{
					id: his.Key,
					rowId: his.Key,
					displayValue: his.ModifiedBy || his.CreatedBy,
					value: his.ModifiedBy || his.CreatedBy,
					columnKey: "editor"
				},
				{
					id: his.Key,
					rowId: his.Key,
					displayValue: his.change,
					value: his.change,
					columnKey: "change"
				}
			]
		})

		setTableData(data)
	}

	useEffect(() => {
		factorChanges(history, eventFlowHistory)
	}, [history, eventFlowHistory])

	return (
		<BrowserRouter>
			<section className='event-flow-history-container'>
				<section>
					<h2>{translations.editHistory}</h2>
				</section>
				<p>{translations.historySubtext}</p>
				<Table rows={tableData} columns={HistoryColumns} key={"history"} pageSize={5} />
			</section>
		</BrowserRouter>
	)
}

export default EventHistoryTable
